import { CloseIcon, SearchIcon } from '@assets/icons';
import { CardButton, IconButton } from '@components/button';
import { Group } from '@components/layout';
import { useConfirmation, useModal } from '@components/modal';
import { Typography } from '@components/typography';
import {
  ServiceTypeEnum,
  useRemoveWorkspaceServicesMutation,
  type Service,
} from '@generated/graphql';
import { Stack } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import posthog from 'posthog-js';
import { ServerTag } from '../../ServerTag';
import { ResourceCard } from '../ResourceCard';
import { useManageServicesModal } from './ManageServicesModal';
import { ServiceDetailsModal } from './ServiceDetailsModal';

type ServiceCardProps = {
  service: Pick<
    Service,
    'name' | 'serviceType' | 'serviceId' | 'description' | 'createdAt' | 'updatedAt'
  >;
  workspaceId?: string;
  onChange?: VoidFunction;
  onClick?: VoidFunction;
};

export const ServiceCard = ({ service, workspaceId, onChange, onClick }: ServiceCardProps) => {
  const { organizationId } = useOrganizationContext();
  const { showConfirmation } = useConfirmation();
  const [removeWorkspaceServices] = useRemoveWorkspaceServicesMutation();
  const handleRemoveService = () => {
    showConfirmation({
      onAffirm: async () => {
        await removeWorkspaceServices({
          variables: {
            workspaceId,
            serviceIds: [service.serviceId],
          },
          onCompleted: () => {
            posthog.capture('services:user_remove-workspace-service', {
              workspaceId,
              serviceId: service.serviceId,
            });
            onChange?.();
          },
        });
      },
      message: `Are you sure you want to detach the service: ${service.name}?`,
    });
  };

  const { showModal: showServiceDetailsModal } = useModal({
    component: ServiceDetailsModal,
    componentProps: { service, organizationId },
    modalProps: { sx: { pt: 7.25 } },
  });

  const handleViewServiceDetails = () => {
    showServiceDetailsModal();
  };

  return (
    <ResourceCard sx={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <Group alignItems="center" gap={2} flexWrap="wrap">
        <Typography variant="body1" handleOverFlow color="foreground.paperCard.default">
          {service.name}
        </Typography>
      </Group>
      <Group alignItems="center" justifyContent="flex-end" flex={1} flexShrink={0} gap={1}>
        <ServerTag
          label="Persistent"
          variant="caption2"
          sx={{
            mr: 1.5,
            visibility:
              service.serviceType === ServiceTypeEnum.PERSISTENT_SERVICE ? 'visible' : 'hidden',
          }}
        />
        <IconButton
          Icon={SearchIcon}
          tooltip="Details"
          onClick={handleViewServiceDetails}
          size={16}
          variant="default"
        />
        <IconButton
          Icon={CloseIcon}
          tooltip="Remove from workspace"
          onClick={handleRemoveService}
          size={16}
          variant="error"
        />
      </Group>
    </ResourceCard>
  );
};

export const AddServiceCard = ({
  workspaceId,
  services,
  onChange,
}: {
  workspaceId: string;
  services: Partial<Service>[];
  onChange?: VoidFunction;
}) => {
  const { organizationId } = useOrganizationContext();

  const hasServices = services.length > 0;

  const { showManageServicesModal } = useManageServicesModal({
    componentProps: {
      organizationId,
      workspaceId,
      onChange,
    },
    modalProps: { title: 'Manage attached services' },
  });

  const handleManageServices = () => {
    showManageServicesModal();
  };

  return (
    <CardButton
      label={hasServices ? 'Manage services' : 'Add services'}
      onClick={handleManageServices}
      background="paperCard"
      sx={{ width: '100%', maxWidth: '800px', minWidth: '200px' }}
      info={
        !hasServices ? (
          <Stack>
            <Typography variant="caption2" textAlign="center">
              You don&apos;t have any services attached to this workspace.
            </Typography>
            <Typography variant="caption2" textAlign="center">
              Click here to add services from your organization or from a list of prebuilt service
              templates.
            </Typography>
          </Stack>
        ) : null
      }
    />
  );
};
